import type { CSSProperties } from "react";

/* Bound Set — the Rulebound mark.
 *
 * Two brackets containing three rules, the first in copper: an open book, and
 * set notation for a defined body of rules. Construction spec lives in
 * docs/LOGO-HANDOFF.md §2; `scripts/gen-logo.mjs` draws the same geometry for
 * the static assets in public/logo/. Change one, change both.
 *
 * Shipped as a component rather than an <img> so it costs no request and can
 * take colour from its variant.
 */

export type MarkVariant = "default" | "reverse" | "bare" | "bare-reverse" | "mono";

const FOREST = "#29483D";
const COPPER = "#B86F44";
const PAPER = "#FCFAF5";
const INK = "#202522";

const VARIANTS: Record<
  MarkVariant,
  { container: string | null; stroke: string; first: string; rest: string }
> = {
  default: { container: FOREST, stroke: PAPER, first: COPPER, rest: PAPER },
  reverse: { container: PAPER, stroke: FOREST, first: COPPER, rest: FOREST },
  bare: { container: null, stroke: FOREST, first: COPPER, rest: FOREST },
  "bare-reverse": { container: null, stroke: PAPER, first: COPPER, rest: PAPER },
  mono: { container: INK, stroke: PAPER, first: PAPER, rest: PAPER },
};

export function RuleboundMark({
  size = 36,
  variant = "default",
  decorative = false,
  className,
  style,
  /** Per-rule opacity override — the loader staggers these in. */
  ruleOpacity,
}: {
  size?: number;
  variant?: MarkVariant;
  /** True when a wordmark sits beside it, so the name isn't announced twice. */
  decorative?: boolean;
  className?: string;
  style?: CSSProperties;
  ruleOpacity?: [number, number, number];
}) {
  // §2 optical compensation: as the mark shrinks, weight steps up so the
  // negative space survives. Rules keep their centre lines.
  const w = size <= 16 ? 4.4 : size < 36 ? 3.8 : 3.2;
  const dy = (3.2 - w) / 2;
  const c = VARIANTS[variant];
  const o = ruleOpacity ?? [1, 0.92, 0.55];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      style={style}
      {...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": "Rulebound" })}
    >
      {c.container && <rect x="2" y="2" width="60" height="60" rx="15" fill={c.container} />}
      <path
        d="M21 15h-5.5v34H21"
        stroke={c.stroke}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M43 15h5.5v34H43"
        stroke={c.stroke}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="24.5" y={20.5 + dy} width="15" height={w} rx={w / 2} fill={c.first} opacity={o[0]} />
      <rect x="24.5" y={30.4 + dy} width="15" height={w} rx={w / 2} fill={c.rest} opacity={o[1]} />
      <rect x="24.5" y={40.3 + dy} width="8.5" height={w} rx={w / 2} fill={c.rest} opacity={o[2]} />
    </svg>
  );
}

/**
 * Mark + wordmark. Composed from a real <span> rather than the lockup SVGs so
 * the wordmark is selectable text in the app's own Newsreader (§1).
 */
export function RuleboundLockup({
  size = 36,
  variant = "default",
  markStyle,
  className,
}: {
  size?: number;
  variant?: MarkVariant;
  markStyle?: CSSProperties;
  className?: string;
}) {
  const ink = variant === "reverse" || variant === "bare-reverse" ? PAPER : INK;
  return (
    <span className={className} style={{ display: "inline-flex", alignItems: "center", gap: size * 0.3 }}>
      <RuleboundMark size={size} variant={variant} decorative style={markStyle} />
      <span
        className="font-serif"
        style={{
          fontWeight: 600,
          fontSize: size * 0.64,
          letterSpacing: "-0.022em",
          color: ink,
          lineHeight: 1,
        }}
      >
        Rulebound
      </span>
    </span>
  );
}
